import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { PlaceIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  Text,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { currentProfileAtom } from "../../../features/Club/atoms";
import { EventDetailPairingsRound } from "./EventDetailPairingsRound";
import { EventDetailPairingItem_booking$key } from "./__generated__/EventDetailPairingItem_booking.graphql";

type EventDetailPairingItemProps = {
  fragmentRef: EventDetailPairingItem_booking$key | null;
};

export const EventDetailPairingItem = ({
  fragmentRef,
}: EventDetailPairingItemProps) => {
  const data = useFragment(EventDetailPairingItemFragment, fragmentRef);
  const currentMemberId = useAtomValue(currentProfileAtom)?.memberId;

  const pairingTime = data?.startSlot?.time
    ? format(parse(data?.startSlot?.time, "HH:mm:ss", new Date()), "h:mm aaa")
    : "";

  const yourRound = useMemo(
    () =>
      data?.rounds?.edges?.find(
        (round) => round?.node?.player?.id === currentMemberId
      ),
    [data?.rounds?.edges, currentMemberId]
  );

  const otherRoundPairs = useMemo(() => {
    const otherRounds = data?.rounds?.edges?.filter(
      (round) => round?.node?.player?.id !== currentMemberId
    );
    return otherRounds
      ? otherRounds.reduce<Array<typeof otherRounds>>((pairs, round, index) => {
          if (index % 2 === 0) {
            pairs.push([round]);
          } else {
            pairs[pairs.length - 1].push(round);
          }
          return pairs;
        }, [])
      : [];
  }, [data?.rounds?.edges, currentMemberId]);

  return (
    <Box marginTop={4} testID="event-detail-pairing-item">
      <VStack
        borderWidth={1.5}
        borderColor="outline-primary"
        borderRadius="4"
        padding={4}
        gap={3}
        backgroundColor="surface-secondary"
      >
        <HStack justifyContent="space-between" alignItems="center">
          <VStack>
            <HStack alignItems="center" gap={1}>
              <Text variant="mainTiny" color="text-secondary">
                {data?.startSlot?.course?.name}
              </Text>
              <PlaceIcon color="icon-disabled" />
            </HStack>
            <Text variant="mainBaseSemiBold">{pairingTime}</Text>
          </VStack>
          <Text variant="mainSmallSemiBold">{t`Your Booking`}</Text>
        </HStack>
        <VStack gap={1}>
          {yourRound && <EventDetailPairingsRound round={yourRound?.node} />}
          <VStack gap={1}>
            {otherRoundPairs.map((pair, pairIndex) => (
              <HStack
                key={`round-pair-${pairIndex}`}
                gap={4}
                flexWrap="wrap"
                alignItems="center"
                testID="pairing-row"
              >
                {pair.map((round) => (
                  <EventDetailPairingsRound
                    key={round?.node?.player?.id ?? `round-${pairIndex}`}
                    round={round?.node}
                  />
                ))}
              </HStack>
            ))}
          </VStack>
        </VStack>
      </VStack>
    </Box>
  );
};

export const EventDetailPairingItemFragment = graphql`
  fragment EventDetailPairingItem_booking on Booking {
    rounds {
      edges {
        node {
          player {
            id
          }
          ...EventDetailPairingsRound_round
        }
      }
    }
    startSlot {
      ... on TimeSlot {
        time
        course {
          name
          courseBucket {
            name
          }
        }

        eventSlot {
          eventSession {
            id
          }
        }
      }
    }
  }
`;
